import { useState } from "react";
import { Calendar, MapPin, Trophy, ImageOff } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { pickPhoto, matchPoster } from "@/assets/photos";
import { Flag, MatchFlags, getArgentinaMatchOpponent } from "@/components/site/flag";
import { Crest } from "@/components/site/crest";
import { Img } from "@/components/site/img";
import { cn } from "@/lib/utils";

function FixturePoster({ src, opponent }: { src: string | null; opponent: string }) {
  const [state, setState] = useState<"loading" | "loaded" | "error">(src ? "loading" : "error");
  return (
    <div className="relative -mx-5 -mt-5 mb-1 aspect-[16/9] overflow-hidden border-b border-border bg-navy">
      {/* Blur-up placeholder — Argentina-themed gradient + shimmer, fades out on load */}
      <div
        aria-hidden
        className={cn(
          "absolute inset-0 transition-opacity duration-500",
          state === "loading" ? "opacity-100" : "opacity-0",
        )}
        style={{
          backgroundImage:
            "radial-gradient(120% 80% at 20% 0%, hsl(var(--sky) / 0.45), transparent 60%), radial-gradient(120% 80% at 100% 100%, hsl(var(--navy) / 0.9), transparent 55%), linear-gradient(135deg, hsl(var(--sky) / 0.25), hsl(var(--navy)))",
          filter: "blur(12px)",
          transform: "scale(1.1)",
        }}
      >
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>
      {src && state !== "error" && (
        <Img
          src={src}
          alt={`Argentina vs ${opponent} match poster`}
          width={1536}
          height={864}
          loading="lazy"
          decoding="async"
          onLoad={() => setState("loaded")}
          onError={() => setState("error")}
          className={cn(
            "h-full w-full object-cover transition-[opacity,transform] duration-500 ease-out",
            state === "loaded" ? "scale-100 opacity-100" : "scale-105 opacity-0",
          )}
        />
      )}
      {state === "error" && (
        <div
          role="img"
          aria-label={`Argentina vs ${opponent} — poster unavailable`}
          className="flex h-full w-full flex-col items-center justify-center gap-2 bg-navy text-primary-foreground/70"
        >
          <ImageOff aria-hidden className="h-7 w-7 text-sky/80" />
          <p className="font-stadium text-2xl tracking-wide">ARG vs {opponent.toUpperCase()}</p>
        </div>
      )}
    </div>
  );
}

// SSR/CSR safe formatting — always UTC to avoid hydration timezone mismatches.
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function fmtDate(iso: string) {
  const d = new Date(iso);
  return `${DOW[d.getUTCDay()]}, ${MON[d.getUTCMonth()]} ${d.getUTCDate()}`;
}
function fmtLong(iso: string) {
  const d = new Date(iso);
  const dow = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][d.getUTCDay()];
  return `${dow}, ${MON[d.getUTCMonth()]} ${d.getUTCDate()} · ${fmtTime(iso)}`;
}
function fmtTime(iso: string) {
  const d = new Date(iso);
  const h = String(d.getUTCHours()).padStart(2, "0");
  const m = String(d.getUTCMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}
function fmtMonthDay(iso: string) {
  const d = new Date(iso);
  return `${MON[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}
export const dateUtils = { fmtDate, fmtLong, fmtTime, fmtMonthDay };

type NewsCardData = {
  id?: string;
  slug?: string | null;
  title: string;
  excerpt: string | null;
  category: string;
  author: string | null;
  published_at: string;
  cover_image?: string | null;
};

const CATEGORY_LABELS: Record<string, string> = {
  europe: "Europe",
  primera: "Primera División",
  rest_of_world: "Rest of World",
  transfers: "Transfers",
  national_team: "National Team",
  articles: "Long Read",
  interviews: "Interview",
  video_games: "Video Games",
  scandals: "Scandals",
};

export function NewsCard({ article, priority = false }: { article: NewsCardData; priority?: boolean }) {
  const img = article.cover_image || pickPhoto(article.id ?? article.title);
  const slug = article.slug ?? article.id ?? "story";
  const categoryLabel = CATEGORY_LABELS[article.category] ?? article.category;
  const matchOpponent = getArgentinaMatchOpponent(`${article.title} ${article.excerpt ?? ""} ${slug}`);
  return (
    <Link
      to="/news/$slug"
      params={{ slug }}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-sky/50 hover:shadow-stadium active:scale-[0.99]"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
        <Img
          src={img}
          alt={article.title}
          priority={priority}
          width={640}
          height={400}
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
        />
      </div>
      <div className="flex flex-1 flex-col gap-2.5 p-4 lg:p-5">
        <MatchFlags opponent={matchOpponent} className="mb-0.5" />
        <div className="flex items-center gap-2 font-condensed text-[0.65rem] font-bold uppercase tracking-[0.25em] text-sky-deep">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary" />
          {categoryLabel}
        </div>
        <h3 className="font-display text-xl leading-[1.05] text-navy transition-colors group-hover:text-primary md:text-[1.4rem]">
          {article.title}
        </h3>
        {article.excerpt && (
          <p className="line-clamp-2 text-sm leading-relaxed text-muted-foreground">
            {article.excerpt}
          </p>
        )}
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3 text-[0.7rem] text-muted-foreground">
          <span className="font-condensed font-semibold uppercase tracking-[0.18em] text-navy/80">
            {article.author ?? "Argentinian.Soccer"}
          </span>
          <time className="font-condensed uppercase tracking-wider">{fmtMonthDay(article.published_at)}</time>
        </div>
      </div>
    </Link>
  );
}


type FixtureData = {
  id?: string;
  opponent: string;
  competition: string | null;
  venue: string | null;
  kickoff: string;
  argentina_score: number | null;
  opponent_score: number | null;
  is_home: boolean | null;
  team_scope?: string | null;
};

const CLUB_COMPS = ["primera", "copa libertadores", "copa sudamericana", "argentine cup"];

function isClubFixture(f: FixtureData) {
  const c = (f.competition ?? "").toLowerCase();
  return CLUB_COMPS.some((x) => c.includes(x));
}

export function FixtureCard({ fixture }: { fixture: FixtureData }) {
  const past = fixture.argentina_score !== null && fixture.opponent_score !== null;
  const club = isClubFixture(fixture);
  const poster = club ? null : matchPoster(fixture.opponent);

  // Always render a Link — the featured image must always lead to a dedicated match page.
  const linkProps = fixture.id
    ? {
        to: "/national-team/fixtures/$id" as const,
        params: { id: fixture.id },
        "aria-label": `Argentina vs ${fixture.opponent} — match article`,
      }
    : {
        to: "/national-team/fixtures" as const,
        "aria-label": `Argentina vs ${fixture.opponent} — fixtures`,
      };

  return (
    <Link
      {...linkProps}
      className="group flex flex-col gap-3 overflow-hidden rounded-xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-stadium active:scale-[0.99]"
    >
      {!club && <FixturePoster src={poster} opponent={fixture.opponent} />}
      <div className="flex items-center justify-between">
        <span className="eyebrow">{fixture.competition ?? "Match"}</span>
        <span className="rounded-sm bg-secondary px-2 py-0.5 font-condensed text-[0.7rem] font-bold uppercase tracking-widest text-secondary-foreground">
          {past ? "Result" : fixture.is_home ? "Home" : "Away"}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-1 items-center gap-2.5 text-left">
          {club ? <Crest team="Argentina" /> : <Flag country="argentina" className="h-6 w-9" />}
          <div>
            <p className="font-display text-lg text-navy">Argentina</p>
            <p className="font-condensed text-xs uppercase tracking-widest text-muted-foreground">
              {fixture.is_home ? "Home" : "Away"}
            </p>
          </div>
        </div>
        <div className="rounded-md bg-primary px-3.5 py-1.5 text-center font-stadium text-3xl text-primary-foreground md:text-4xl md:px-4 md:py-2">
          {past
            ? `${fixture.argentina_score} – ${fixture.opponent_score}`
            : fmtTime(fixture.kickoff)}
        </div>
        <div className="flex flex-1 items-center justify-end gap-2.5 text-right">
          <div>
            <p className="font-display text-lg text-navy">{fixture.opponent}</p>
            <p className="font-condensed text-xs uppercase tracking-widest text-muted-foreground">
              {fixture.is_home ? "Away" : "Home"}
            </p>
          </div>
          {/* Always show the opponent's national flag for international fixtures. */}
          {club ? <Crest team={fixture.opponent} /> : <Flag country={fixture.opponent} className="h-6 w-9" />}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 pt-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5" /> {fmtDate(fixture.kickoff)}
        </span>
        {fixture.venue && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" /> {fixture.venue}
          </span>
        )}
      </div>
    </Link>
  );
}

export function StatTile({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-border bg-card p-5">
      <div className="absolute inset-x-0 top-0 h-1 sky-bar" />
      <p className="eyebrow">{label}</p>
      <p className="mt-2 font-stadium text-5xl text-navy">{value}</p>
      {sub && <p className="mt-1 font-condensed text-xs uppercase tracking-wider text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function TrophyBanner({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-sand/60 bg-sand/30 px-4 py-3 text-sm text-navy">
      <Trophy className="h-5 w-5" />
      <p>{text}</p>
    </div>
  );
}
